import { apiClient } from "../api/client";
import type { SessionDirectory } from "./directory";
import { saveResumeState, type UploadResumeState, type UploadedPartState } from "./resume";

const SOURCE_MARKER = "_SUCCESS";

export interface UploadProgress {
  filePath: string;
  uploadedBytes: number;
  totalBytes: number;
  percent: number;
}

export async function uploadSession(
  directory: SessionDirectory,
  state: UploadResumeState,
  onProgress: (progress: UploadProgress) => void,
): Promise<void> {
  if (state.instructions.strategy === "local_put") await uploadLocal(directory, state, onProgress);
  else await uploadOss(directory, state, onProgress);
}

async function uploadLocal(
  directory: SessionDirectory,
  state: UploadResumeState,
  onProgress: (progress: UploadProgress) => void,
): Promise<void> {
  const instructions = state.instructions;
  if (instructions.strategy !== "local_put") return;
  for (const item of instructions.files) {
    if (state.completedFiles.includes(item.path)) continue;
    const file = fileForPath(directory, item.path);
    await apiClient.authorizedUpload(item.url, file, (loaded) => emitProgress(directory, state, item.path, loaded, onProgress));
    markFileComplete(state, item.path);
  }
}

async function uploadOss(
  directory: SessionDirectory,
  state: UploadResumeState,
  onProgress: (progress: UploadProgress) => void,
): Promise<void> {
  const instructions = state.instructions;
  if (instructions.strategy !== "oss_multipart") return;
  for (const item of instructions.files) {
    if (!state.completedFiles.includes(item.path)) {
      await uploadMultipart(directory, state, item.path, instructions.part_size_bytes, onProgress);
    }
  }
  await uploadSourceMarker(directory, state, onProgress);
}

async function uploadMultipart(
  directory: SessionDirectory,
  state: UploadResumeState,
  path: string,
  partSize: number,
  onProgress: (progress: UploadProgress) => void,
): Promise<void> {
  const file = fileForPath(directory, path);
  const parts = state.parts[path] ?? [];
  for (let offset = 0, partNumber = 1; offset < file.size; offset += partSize, partNumber += 1) {
    if (parts.some((part) => part.partNumber === partNumber)) continue;
    const blob = file.slice(offset, Math.min(offset + partSize, file.size));
    const { url } = await apiClient.signPart(state.sessionId, path, partNumber);
    const base = completedPartBytes(parts);
    const etag = await apiClient.signedUpload(url, blob, (loaded) => emitProgress(directory, state, path, base + loaded, onProgress));
    parts.push({ partNumber, etag, size: blob.size });
    state.parts[path] = parts;
    saveResumeState(state);
  }
  await completeMultipart(state, path, parts);
}

async function completeMultipart(state: UploadResumeState, path: string, parts: UploadedPartState[]): Promise<void> {
  const payload = parts
    .sort((left, right) => left.partNumber - right.partNumber)
    .map((part) => ({ part_number: part.partNumber, etag: part.etag }));
  await apiClient.completeMultipart(state.sessionId, path, payload);
  markFileComplete(state, path);
}

async function uploadSourceMarker(
  directory: SessionDirectory,
  state: UploadResumeState,
  onProgress: (progress: UploadProgress) => void,
): Promise<void> {
  if (state.completedFiles.includes(SOURCE_MARKER)) return;
  const { url } = await apiClient.signPut(state.sessionId, SOURCE_MARKER);
  await apiClient.signedUpload(url, directory.sourceMarker, (loaded) => {
    emitProgress(directory, state, SOURCE_MARKER, loaded, onProgress);
  });
  await apiClient.markUploaded(state.sessionId, SOURCE_MARKER);
  markFileComplete(state, SOURCE_MARKER);
}

function fileForPath(directory: SessionDirectory, path: string): File {
  if (path === SOURCE_MARKER) return directory.sourceMarker;
  const file = directory.files.get(path);
  if (!file) throw new Error(`目录中找不到文件：${path}`);
  return file;
}

function markFileComplete(state: UploadResumeState, path: string): void {
  if (!state.completedFiles.includes(path)) state.completedFiles.push(path);
  saveResumeState(state);
}

function emitProgress(
  directory: SessionDirectory,
  state: UploadResumeState,
  path: string,
  activeLoaded: number,
  listener: (progress: UploadProgress) => void,
): void {
  const completed = state.completedFiles.reduce((sum, item) => sum + fileForPath(directory, item).size, 0);
  const uploadedBytes = Math.min(directory.totalBytes, completed + activeLoaded);
  listener({ filePath: path, uploadedBytes, totalBytes: directory.totalBytes, percent: progressPercent(uploadedBytes, directory.totalBytes) });
}

function completedPartBytes(parts: UploadedPartState[]): number {
  return parts.reduce((sum, part) => sum + part.size, 0);
}

function progressPercent(loaded: number, total: number): number {
  return total > 0 ? Math.min(100, Math.round((loaded / total) * 1000) / 10) : 100;
}
